import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  Chip,
  Stack,
  Button,
  Paper
} from '@mui/material';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

const SearchFilters = ({ 
  searchTerm, 
  onSearchChange,
  selectedSection,
  onSectionChange,
  sections,
  filters,
  onFilterChange,
  boosterNames
}) => {
  // Generate ATK/DEF values (0 to 5000 in steps of 100)
  const atkDefValues = Array.from({ length: 51 }, (_, i) => i * 100);
  
  // Generate level values (1 to 12)
  const levelValues = Array.from({ length: 12 }, (_, i) => i + 1);

  // Card types
  const cardTypes = [
    'Normal Monster Card',
    'Effect Monster Card',
    'Fusion Monster Card',
    'Ritual Monster Card',
    'Magic Card',
    'Trap Card'
  ];

  const boosterOptions = [
    'any',
    'Battle Ox',
    'Black Luster Soldier',
    'Blue-Eyes Toon Dragon',
    'Blue-Eyes Ultimate Dragon',
    'Blue-Eyes White Dragon',
    'Buster Blader',
    'Dark Magician',
    'Exodia',
    'Garoozis',
    'Gate Guardian',
    'Gemini Elf',
    'Great Moth',
    'Harpie Lady',
    'Judge Man',
    'Launcher Spider',
    'Millennium Eye',
    'Millennium Puzzle Blue',
    'Millennium Puzzle Green',
    'Millennium Puzzle Yellow',
    'Mystical Elf',
    'Red-Eyes B. Dragon',
    'Relinquished',
    'Tiger Axe',
    'Weekly Yu-Gi-Oh!',
    'Yu-Gi-Oh! Magazine',
  ];

  filters.booster = filters.booster || 'any';

  const handleClearFilters = () => {
    onFilterChange('atkMin', 'any');
    onFilterChange('atkMax', 'any');
    onFilterChange('defMin', 'any');
    onFilterChange('defMax', 'any');
    onFilterChange('levelMin', 'any');
    onFilterChange('levelMax', 'any');
    onFilterChange('cardType', 'any');
    onFilterChange('booster', 'any');
    onSearchChange('');
    onSectionChange('');
  };

  const handleCardTypeChange = (value) => {
    onFilterChange('cardType', value);
    // Reset numeric filters if Magic or Trap card is selected
    if (value === 'Magic Card' || value === 'Trap Card') {
      onFilterChange('atkMin', 'any');
      onFilterChange('atkMax', 'any');
      onFilterChange('defMin', 'any');
      onFilterChange('defMax', 'any');
      onFilterChange('levelMin', 'any');
      onFilterChange('levelMax', 'any');
    }
  };

  const handleBoosterChange = (value) => {
    onFilterChange('booster', value);
  };

  const isNumericFiltersDisabled = filters.cardType === 'Magic Card' || filters.cardType === 'Trap Card';

  const hasActiveFilters = () => {
    return searchTerm ||
      selectedSection ||
      Object.values(filters).some(value => value !== 'any');
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mb: 4 }}>
      {/* Search and Clear Filters */}
      <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
        <Grid item xs={12} md={9}>
          <TextField
            fullWidth
            label="Search cards..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<FilterAltOffIcon />}
            onClick={handleClearFilters}
            disabled={!hasActiveFilters()}
          >
            Clear Filters
          </Button>
        </Grid>
      </Grid>

      {/* Letter Filters */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack 
          direction="row" 
          spacing={1} 
          sx={{ 
            overflowX: 'auto', 
            pb: 0.5,
            justifyContent: 'center',
          }}
        >
          <Chip 
            label="All"
            color={!selectedSection ? "primary" : "default"}
            onClick={() => onSectionChange('')}
            sx={{ cursor: 'pointer' }}
          />
          {sections.map((section) => (
            <Chip
              key={section}
              label={section}
              color={selectedSection === section ? "primary" : "default"}
              onClick={() => onSectionChange(section)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Stack>
      </Paper>

      {/* Numeric Filters */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Card Type Filter */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Card Type
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Card Type</InputLabel>
              <Select
                value={filters.cardType}
                label="Card Type"
                onChange={(e) => handleCardTypeChange(e.target.value)}
              >
                <MenuItem value="any">Any</MenuItem>
                {cardTypes.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Booster Filter */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Booster
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Booster</InputLabel>
              <Select
                value={filters.booster}
                label="Booster"
                onChange={(e) => handleBoosterChange(e.target.value)}
              >
                {boosterOptions.map((booster) => (
                  <MenuItem key={booster} value={booster}>{booster}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* ATK Filter */}
          <Grid item xs={12} md={4}>
            <Typography 
              variant="subtitle2" 
              color={isNumericFiltersDisabled ? "text.disabled" : "text.secondary"} 
              gutterBottom
            >
              ATK Range
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Min ATK</InputLabel>
                  <Select
                    value={isNumericFiltersDisabled ? 'any' : filters.atkMin}
                    label="Min ATK"
                    onChange={(e) => onFilterChange('atkMin', e.target.value)}
                    disabled={isNumericFiltersDisabled}
                    MenuProps={{ 
                      PaperProps: { 
                        style: { maxHeight: 300 },
                        sx: { '& .MuiMenuItem-root': { py: 0.5 } }
                      } 
                    }}
                  >
                    <MenuItem value="any">Any</MenuItem>
                    {atkDefValues.map(value => (
                      <MenuItem key={value} value={value}>{value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Max ATK</InputLabel>
                  <Select
                    value={isNumericFiltersDisabled ? 'any' : filters.atkMax}
                    label="Max ATK"
                    onChange={(e) => onFilterChange('atkMax', e.target.value)}
                    disabled={isNumericFiltersDisabled}
                    MenuProps={{ 
                      PaperProps: { 
                        style: { maxHeight: 300 },
                        sx: { '& .MuiMenuItem-root': { py: 0.5 } }
                      } 
                    }}
                  >
                    <MenuItem value="any">Any</MenuItem>
                    {atkDefValues.map(value => (
                      <MenuItem key={value} value={value}>{value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          {/* DEF Filter */}
          <Grid item xs={12} md={4}>
            <Typography 
              variant="subtitle2" 
              color={isNumericFiltersDisabled ? "text.disabled" : "text.secondary"} 
              gutterBottom
            >
              DEF Range
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Min DEF</InputLabel>
                  <Select
                    value={isNumericFiltersDisabled ? 'any' : filters.defMin}
                    label="Min DEF"
                    onChange={(e) => onFilterChange('defMin', e.target.value)}
                    disabled={isNumericFiltersDisabled}
                    MenuProps={{ 
                      PaperProps: { 
                        style: { maxHeight: 300 },
                        sx: { '& .MuiMenuItem-root': { py: 0.5 } }
                      } 
                    }}
                  >
                    <MenuItem value="any">Any</MenuItem>
                    {atkDefValues.map(value => (
                      <MenuItem key={value} value={value}>{value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Max DEF</InputLabel>
                  <Select
                    value={isNumericFiltersDisabled ? 'any' : filters.defMax}
                    label="Max DEF"
                    onChange={(e) => onFilterChange('defMax', e.target.value)}
                    disabled={isNumericFiltersDisabled}
                    MenuProps={{ 
                      PaperProps: { 
                        style: { maxHeight: 300 },
                        sx: { '& .MuiMenuItem-root': { py: 0.5 } }
                      } 
                    }}
                  >
                    <MenuItem value="any">Any</MenuItem>
                    {atkDefValues.map(value => (
                      <MenuItem key={value} value={value}>{value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          {/* Level Filter */}
          <Grid item xs={12} md={4}>
            <Typography 
              variant="subtitle2" 
              color={isNumericFiltersDisabled ? "text.disabled" : "text.secondary"} 
              gutterBottom
            >
              Level Range
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Min Level</InputLabel>
                  <Select
                    value={isNumericFiltersDisabled ? 'any' : filters.levelMin}
                    label="Min Level"
                    onChange={(e) => onFilterChange('levelMin', e.target.value)}
                    disabled={isNumericFiltersDisabled}
                    MenuProps={{ 
                      PaperProps: { 
                        style: { maxHeight: 300 },
                        sx: { '& .MuiMenuItem-root': { py: 0.5 } }
                      } 
                    }}
                  >
                    <MenuItem value="any">Any</MenuItem>
                    {levelValues.map(value => (
                      <MenuItem key={value} value={value}>{value}★</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Max Level</InputLabel>
                  <Select
                    value={isNumericFiltersDisabled ? 'any' : filters.levelMax}
                    label="Max Level"
                    onChange={(e) => onFilterChange('levelMax', e.target.value)}
                    disabled={isNumericFiltersDisabled}
                    MenuProps={{ 
                      PaperProps: { 
                        style: { maxHeight: 300 },
                        sx: { '& .MuiMenuItem-root': { py: 0.5 } }
                      } 
                    }}
                  >
                    <MenuItem value="any">Any</MenuItem>
                    {levelValues.map(value => (
                      <MenuItem key={value} value={value}>{value}★</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SearchFilters;
